// FICHA DEL PRODUCTO (pestaña de OBJETO), con las pestañas del maestro de Random (pedido de Rodrigo,
// 2026-10-13): Producto (identidad, unidades RTU y códigos) · Clasificación (súper familia ›
// familia › subfamilia y marca) · Precios (todas las listas: unidad, caja y escalas) · Impuestos ·
// Dimensiones (peso, volumen, contenido) · Stock por ubicación. Quien tiene permiso edita aquí mismo;
// el stock deriva del libro y no se edita.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/cliente';
import { claves, useProducto, useUbicaciones } from '../api/consultas';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { decimal, fecha, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import {
  aApi,
  aEdicion,
  caminoDe,
  homologar,
  hijasDe,
  nombreNivel,
  textoEscalas,
  useArbolCategorias,
  useMarcasFila,
  usePreciosDeProducto,
  valorDigitado,
} from '../precios/datos';
import { TablaStock } from '../ventas/BuscadorProducto';

type Seccion = 'producto' | 'clasificacion' | 'precios' | 'impuestos' | 'dimensiones' | 'stock';
const SECCIONES: ReadonlyArray<readonly [Seccion, string]> = [
  ['producto', 'Producto'],
  ['clasificacion', 'Clasificación'],
  ['precios', 'Precios'],
  ['impuestos', 'Impuestos'],
  ['dimensiones', 'Dimensiones'],
  ['stock', 'Stock'],
];
const t = (v: unknown) => (v === null || v === undefined ? '' : String(v));

export function FichaProducto({
  productoId,
  seccionInicial = 'producto',
}: {
  productoId: string;
  seccionInicial?: Seccion;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const q = useProducto(empresaId, productoId);
  const ubicaciones = useUbicaciones(empresaId);
  const [seccion, setSeccion] = useState<Seccion>(seccionInicial);
  const qc = useQueryClient();
  const intentos = useIntentos();
  // Un PATCH a la ficha (clasificación o dimensiones), con la versión que se está viendo.
  const actualizar = useMutation({
    mutationFn: (cambios: Record<string, unknown>) => {
      const cuerpo = { empresaId, version: q.data!.producto.version, ...cambios };
      return api.patch(`/productos/${productoId}`, cuerpo, {
        idempotencia: intentos.clave('ficha-producto', cuerpo),
      });
    },
    onSuccess: async () => {
      intentos.cerrar();
      await qc.invalidateQueries({ queryKey: claves.producto(empresaId, productoId) });
      await qc.invalidateQueries({ queryKey: ['config', 'productos'] });
    },
    onError: (e) => intentos.cerrar(e),
  });
  if (q.isError) return <PanelError error={q.error} />;
  if (!q.data) return <p className="apagado pantalla">Cargando producto…</p>;
  const { producto: p, presentaciones } = q.data;
  const gestiona = puede('catalogo.productos.gestionar');
  return (
    <div className="pantalla ficha-producto" data-pantalla="producto">
      <header className="encabezado-entidad">
        <span className="ruta-migas">Productos › Ficha</span>
        <h2>
          <span className="mono">{p.sku}</span> · {p.nombre}
          {p.estado && p.estado !== 'activo' && (
            <span className="insignia tono-aviso"> {p.estado}</span>
          )}
        </h2>
        <p className="apagado">
          Unidad base <b>{p.unidad_base_codigo}</b>
          {p.venta_multiplo
            ? ` · se vende solo en múltiplos de ${decimal(p.venta_multiplo)} ${p.unidad_base_codigo}`
            : ' · venta libre (sin múltiplo)'}
        </p>
      </header>
      <div className="secciones-od" role="tablist" aria-label="Secciones de la ficha">
        {SECCIONES.map(([id, n]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={seccion === id}
            className={seccion === id ? 'activa' : ''}
            onClick={() => setSeccion(id)}
          >
            {n}
          </button>
        ))}
      </div>
      {actualizar.isError && <PanelError error={actualizar.error} />}
      <div className="ficha-producto-cuerpo">
        {seccion === 'producto' && (
          <>
            <section aria-label="Identidad">
              <h3>Producto</h3>
              <dl className="datos-ficha">
                <dt>SKU</dt>
                <dd className="mono">{p.sku}</dd>
                <dt>Descripción</dt>
                <dd>{p.nombre}</dd>
                <dt>Nombre corto</dt>
                <dd>{p.nombre_corto || '—'}</dd>
                <dt>Tipo</dt>
                <dd>{p.tipo ?? '—'}</dd>
                <dt>Estado</dt>
                <dd>{p.estado ?? '—'}</dd>
                <dt>Retornable</dt>
                <dd>{p.retornable ? 'sí' : 'no'}</dd>
              </dl>
            </section>
            <section aria-label="Presentaciones">
              <h3>Unidades (RTU)</h3>
              <table className="grilla compacta" aria-label="Presentaciones">
                <thead>
                  <tr>
                    <th>Presentación</th>
                    <th>Unidad</th>
                    <th className="al-der">Unidades base</th>
                    <th>Vendible</th>
                    <th>Activa</th>
                  </tr>
                </thead>
                <tbody>
                  {presentaciones.map((x) => (
                    <tr key={x.id}>
                      <td>
                        {x.nombre} <span className="apagado">({x.codigo})</span>
                        {x.es_base && <span className="apagado"> · base</span>}
                      </td>
                      <td>{x.unidad_codigo}</td>
                      <td className="al-der mono">{decimal(x.factor_a_base)}</td>
                      <td>{x.vendible ? 'sí' : 'no'}</td>
                      <td>{x.activa ? 'sí' : 'no'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <section aria-label="Códigos">
              <h3>Códigos</h3>
              <table className="grilla compacta" aria-label="Códigos">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Código</th>
                    <th>Principal</th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data.codigos ?? []).map((c) => (
                    <tr key={t(c['id'])}>
                      <td>{t(c['tipo'])}</td>
                      <td className="mono">{t(c['valor'])}</td>
                      <td>{c['principal'] ? 'sí' : ''}</td>
                    </tr>
                  ))}
                  {!q.data.codigos?.length && (
                    <tr>
                      <td colSpan={3} className="grilla-vacia">
                        Sin códigos de barra ni de proveedor
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          </>
        )}
        {seccion === 'clasificacion' && (
          <Clasificacion
            categoriaId={p.categoria_id ?? null}
            marcaId={p.marca_id ?? null}
            editable={gestiona}
            pendiente={actualizar.isPending}
            alGuardar={(c) => actualizar.mutate(c)}
          />
        )}
        {seccion === 'precios' && (
          <PreciosDelProducto
            productoId={productoId}
            presentaciones={presentaciones}
            editable={puede('precios.precios.gestionar')}
          />
        )}
        {seccion === 'impuestos' && (
          <section aria-label="Impuestos">
            <h3>Impuestos</h3>
            <p>
              <b>IVA</b> · tasa general, se aplica a todo producto afecto.
            </p>
            <table className="grilla compacta" aria-label="Impuestos adicionales">
              <thead>
                <tr>
                  <th>Impuesto adicional</th>
                  <th>Desde</th>
                  <th>Hasta</th>
                </tr>
              </thead>
              <tbody>
                {(q.data.impuestos ?? []).map((i, n) => (
                  <tr key={n}>
                    <td className="mono">{t(i['impuesto_codigo'])}</td>
                    <td>{i['vigencia_desde'] ? fecha(t(i['vigencia_desde'])) : '—'}</td>
                    <td>{i['vigencia_hasta'] ? fecha(t(i['vigencia_hasta'])) : 'vigente'}</td>
                  </tr>
                ))}
                {!q.data.impuestos?.length && (
                  <tr>
                    <td colSpan={3} className="grilla-vacia">
                      Sin impuesto adicional (ILA u otro)
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
        )}
        {seccion === 'dimensiones' && (
          <Dimensiones
            p={p}
            editable={gestiona}
            pendiente={actualizar.isPending}
            alGuardar={(c) => actualizar.mutate(c)}
          />
        )}
        {seccion === 'stock' && (
          <section aria-label="Stock">
            <h3>Stock por ubicación</h3>
            <TablaStock
              empresaId={empresaId}
              productoId={p.id}
              ubicaciones={ubicaciones.data ?? []}
              unidad={p.unidad_base_codigo}
            />
          </section>
        )}
      </div>
    </div>
  );
}

function Clasificacion({
  categoriaId,
  marcaId,
  editable,
  pendiente,
  alGuardar,
}: {
  categoriaId: string | null;
  marcaId: string | null;
  editable: boolean;
  pendiente: boolean;
  alGuardar: (c: { categoriaId: string | null; marcaId: string | null }) => void;
}) {
  const categorias = useArbolCategorias();
  const marcas = useMarcasFila();
  const cats = useMemo(() => categorias.data ?? [], [categorias.data]);
  const porId = useMemo(() => new Map(cats.map((c) => [c.id, c])), [cats]);
  const camino = caminoDe(categoriaId, porId);
  const [sel, setSel] = useState<string[]>([]);
  const [marca, setMarca] = useState(marcaId ?? '');
  useEffect(() => {
    setSel(camino.map((c) => c.id));
    setMarca(marcaId ?? '');
  }, [categoriaId, marcaId, cats.length]);
  const elegida = sel[sel.length - 1] ?? null;
  const cambio = elegida !== categoriaId || (marca || null) !== marcaId;
  const niveles = [0, 1, 2];
  return (
    <section aria-label="Clasificación">
      <h3>Clasificación</h3>
      <div className="clasificacion-ficha">
        {niveles.map((n) => {
          const padre = n === 0 ? null : (sel[n - 1] ?? undefined);
          const opciones = padre === undefined ? [] : hijasDe(cats, padre);
          return (
            <label key={n} className="campo">
              <span className="etiqueta">{nombreNivel(n + 1)}</span>
              <select
                aria-label={nombreNivel(n + 1)}
                value={sel[n] ?? ''}
                disabled={!editable || padre === undefined || (n > 0 && !opciones.length)}
                onChange={(e) =>
                  setSel((s) => [...s.slice(0, n), ...(e.target.value ? [e.target.value] : [])])
                }
              >
                <option value="">{n === 0 ? 'Sin clasificar' : '—'}</option>
                {opciones.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre} ({c.codigo})
                  </option>
                ))}
              </select>
            </label>
          );
        })}
        <label className="campo">
          <span className="etiqueta">Marca</span>
          <select
            aria-label="Marca"
            value={marca}
            disabled={!editable}
            onChange={(e) => setMarca(e.target.value)}
          >
            <option value="">Sin marca</option>
            {(marcas.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.nombre}
              </option>
            ))}
          </select>
        </label>
      </div>
      {editable && (
        <div className="acciones-ficha">
          <button
            type="button"
            className="primario"
            disabled={!cambio || pendiente}
            onClick={() => alGuardar({ categoriaId: elegida, marcaId: marca || null })}
          >
            {pendiente ? 'Guardando…' : 'Guardar clasificación'}
          </button>
        </div>
      )}
    </section>
  );
}

function Dimensiones({
  p,
  editable,
  pendiente,
  alGuardar,
}: {
  p: {
    peso_kg?: string | null;
    volumen_m3?: string | null;
    contenido_neto?: string | null;
    contenido_unidad_codigo?: string | null;
  };
  editable: boolean;
  pendiente: boolean;
  alGuardar: (c: Record<string, unknown>) => void;
}) {
  const inicial = {
    peso: p.peso_kg ? String(Number(p.peso_kg)) : '',
    volumen: p.volumen_m3 ? String(Number(p.volumen_m3)) : '',
    contenido: p.contenido_neto ? String(Number(p.contenido_neto)) : '',
    unidad: p.contenido_unidad_codigo ?? '',
  };
  const [v, setV] = useState(inicial);
  useEffect(
    () => setV(inicial),
    [p.peso_kg, p.volumen_m3, p.contenido_neto, p.contenido_unidad_codigo],
  );
  const num = (x: string) => (x.trim() === '' ? null : x.trim().replace(',', '.'));
  const ok = (x: string) => x.trim() === '' || /^\d+([.,]\d+)?$/.test(x.trim());
  const cambio = JSON.stringify(v) !== JSON.stringify(inicial);
  const campo = (k: keyof typeof v, etiqueta: string, ayuda: string) => (
    <label className="campo">
      <span className="etiqueta">{etiqueta}</span>
      <input
        aria-label={etiqueta}
        value={v[k]}
        disabled={!editable}
        placeholder={ayuda}
        onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))}
      />
    </label>
  );
  return (
    <section aria-label="Dimensiones">
      <h3>Dimensiones</h3>
      <p className="apagado">
        El peso por unidad base calcula los kilos de cada nota y la ocupación del camión.
      </p>
      <div className="clasificacion-ficha">
        {campo('peso', 'Peso (kg por unidad)', 'ej. 1,6')}
        {campo('volumen', 'Volumen (m³ por unidad)', 'ej. 0,002')}
        {campo('contenido', 'Contenido neto', 'ej. 1500')}
        {campo('unidad', 'Unidad del contenido', 'ej. ML')}
      </div>
      {editable && (
        <div className="acciones-ficha">
          <button
            type="button"
            className="primario"
            disabled={!cambio || pendiente || !ok(v.peso) || !ok(v.volumen) || !ok(v.contenido)}
            onClick={() =>
              alGuardar({
                pesoKg: num(v.peso),
                volumenM3: num(v.volumen),
                contenidoNeto: num(v.contenido),
                contenidoUnidadCodigo: v.unidad.trim() ? v.unidad.trim().toUpperCase() : null,
              })
            }
          >
            {pendiente ? 'Guardando…' : 'Guardar dimensiones'}
          </button>
        </div>
      )}
    </section>
  );
}

/** Todas las listas de la empresa en un cuadro: unidad, caja y escalas. Clic en un precio = cambiarlo. */
function PreciosDelProducto({
  productoId,
  presentaciones,
  editable,
}: {
  productoId: string;
  presentaciones: Array<{
    id: string;
    codigo: string;
    es_base: boolean;
    factor_a_base: string;
    vendible: boolean;
    activa: boolean;
  }>;
  editable: boolean;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const q = usePreciosDeProducto(empresaId, productoId);
  const qc = useQueryClient();
  const intentos = useIntentos();
  const vendibles = presentaciones.filter((x) => x.vendible && x.activa);
  const unidad = vendibles.find((x) => x.es_base) ?? vendibles[0];
  const caja = vendibles
    .filter((x) => x !== unidad)
    .sort((a, b) => Number(b.factor_a_base) - Number(a.factor_a_base))[0];
  const columnas = [unidad, caja].filter((x): x is NonNullable<typeof x> => !!x);
  const [editando, setEditando] = useState<string | null>(null);
  const [ligados, setLigados] = useState(true);
  const [texto, setTexto] = useState('');
  const fijar = useMutation({
    // Unidad y caja juntas (homologadas) en un solo comando: o se guardan las dos o ninguna.
    mutationFn: (
      precios: Array<{
        listaId: string;
        presentacionId: string;
        valor: string;
        escalas: ReturnType<typeof aApi>;
      }>,
    ) => {
      const cuerpo = {
        empresaId,
        motivo: 'Cambio desde la ficha del producto',
        precios: precios.map((c) => ({ ...c, productoId })),
      };
      return api.post('/precios/lote', cuerpo, {
        idempotencia: intentos.clave('precio-ficha', cuerpo),
      });
    },
    onSuccess: async () => {
      intentos.cerrar();
      setEditando(null);
      await qc.invalidateQueries({ queryKey: ['precios'] });
    },
    onError: (e) => intentos.cerrar(e),
  });
  if (q.isError) return <PanelError error={q.error} />;
  return (
    <section aria-label="Precios">
      <h3>Precios por lista</h3>
      {editable && columnas.length > 1 && (
        <label className="casilla-filtro">
          <input type="checkbox" checked={ligados} onChange={(e) => setLigados(e.target.checked)} />{' '}
          Homologar unidad ↔ caja (la otra se fija por el factor)
        </label>
      )}
      {fijar.isError && <PanelError error={fijar.error} />}
      <table className="grilla compacta" aria-label="Precios por lista">
        <thead>
          <tr>
            <th>Lista</th>
            {columnas.map((x) => (
              <th key={x.id} className="der">
                {x.es_base ? 'Unidad' : `${x.codigo} ×${Number(x.factor_a_base)}`}
              </th>
            ))}
            <th>Escalas</th>
            <th>Vigente desde</th>
          </tr>
        </thead>
        <tbody>
          {(q.data ?? []).map((l) => {
            const de = (presId: string) => l.precios.find((x) => x.presentacionId === presId);
            const escalas = columnas
              .map((x) => {
                const pr = de(x.id);
                return pr?.escalas.length
                  ? `${x.es_base ? '' : `${x.codigo}: `}${textoEscalas(pr.escalas)}`
                  : '';
              })
              .filter(Boolean)
              .join(' | ');
            const desde = l.precios
              .map((x) => x.desde)
              .filter(Boolean)
              .sort()
              .pop();
            return (
              <tr key={l.listaId}>
                <td>
                  <span className="mono">{l.codigo}</span> {l.nombre}
                  {l.uso !== 'venta' && <span className="apagado"> · {l.uso}</span>}
                </td>
                {columnas.map((x) => {
                  const pr = de(x.id);
                  const k = `${l.listaId}|${x.id}`;
                  if (editando === k)
                    return (
                      <td key={x.id} className="der">
                        <input
                          autoFocus
                          className="entrada-precio"
                          aria-label={`Nuevo precio ${l.codigo} ${x.codigo}`}
                          value={texto}
                          onChange={(e) => setTexto(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') setEditando(null);
                            if (e.key !== 'Enter') return;
                            const v = valorDigitado(texto);
                            if (v === null) return;
                            const lote = [
                              {
                                listaId: l.listaId,
                                presentacionId: x.id,
                                valor: v,
                                escalas: aApi(aEdicion(pr?.escalas ?? [])),
                              },
                            ];
                            // La otra presentación (caja ↔ unidad) se fija por el factor.
                            const otra = ligados ? columnas.find((y) => y !== x) : undefined;
                            const po = otra && de(otra.id);
                            if (otra && Number(v) !== Number(pr?.valor ?? -1))
                              lote.push({
                                listaId: l.listaId,
                                presentacionId: otra.id,
                                valor: homologar(v, x.factor_a_base, otra.factor_a_base),
                                escalas: aApi(aEdicion(po?.escalas ?? [])),
                              });
                            fijar.mutate(lote);
                          }}
                          onBlur={() => !fijar.isPending && setEditando(null)}
                        />
                      </td>
                    );
                  return (
                    <td key={x.id} className="der">
                      {editable ? (
                        <button
                          type="button"
                          className="valor-editable"
                          aria-label={`Precio ${l.codigo} ${x.codigo}`}
                          onClick={() => {
                            setTexto(pr ? String(Number(pr.valor)) : '');
                            setEditando(k);
                          }}
                        >
                          {pr ? pesos(pr.valor) : <span className="apagado">sin precio</span>}
                        </button>
                      ) : pr ? (
                        pesos(pr.valor)
                      ) : (
                        <span className="apagado">—</span>
                      )}
                    </td>
                  );
                })}
                <td className="apagado">{escalas}</td>
                <td>{desde ? fecha(desde) : '—'}</td>
              </tr>
            );
          })}
          {!q.data?.length && (
            <tr>
              <td colSpan={columnas.length + 3} className="grilla-vacia">
                {q.isLoading ? 'Cargando…' : 'Sin listas en esta empresa'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <p className="apagado">
        Las escalas y los cambios masivos se trabajan en Ventas › Precios por lista (tratamiento de
        listas).
      </p>
    </section>
  );
}
